import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { BannerPreview } from '../../components/BannerPreview/BannerPreview';
import { FeedFilterStatusCell } from '../../components/FeedFilterStatusCell/FeedFilterStatusCell';
import { GridFeedFilterNameCell } from '../../components/GridFeedFilterNameCell/GridFeedFilterNameCell';
import { GridParentCell } from '../../components/GridParentCell/GridParentCell';
import { TextCell } from '../../components/TextCell/TextCell';
import { FeedFilterPriorityCellEditorContainer } from '../../containers/FeedFilterPriorityCellEditorContainer/FeedFilterPriorityCellEditorContainer';
import { FeedFilterRateCellEditorContainer } from '../../containers/FeedFilterRateCellEditorContainer/FeedFilterRateCellEditorContainer';
import { GridActionsMenuContainer } from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { GridBannerPreviewCellContainer } from '../../containers/GridBannerPreviewCellContainer/GridBannerPreviewCellContainer';
import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { isTextBannerCheck } from '../../service/BannerHelpers/BannerHelpers';
import { arrangeInOrder } from '../../service/DeclarationHelper';
import { getFunnelTypeName } from '../../service/FeedFiltersHelpers/FeedFiltersHelpers';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import {
    ColumnsSettings,
    ColumnsSettingsViewDict,
    feedFilters
} from '../../typings/common';
import { FeedFilter } from '../../typings/domain';
import { FeedFilterRateType } from '../../typings/feedFilters';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridFeedFilterItemActions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { SmartGroupBudgetCalculation } from '../../typings/groups';
import { getStaticStatsColumns, StatKey } from '../stats/stats';

import * as keyset from './feedFilter.i18n';

const i18n = i18nFactory(keyset);

export enum GridFeedFiltersColumnKey {
    name = 'name',
    status = 'status',
    settings = 'settings',
    targetFunnel = 'targetFunnel',
    cpc = 'cpc',
    cpa = 'cpa',
    autobudgetPriority = 'autobudgetPriority',
    campaignGroup = 'campaignGroup',
    banners = 'banners'
}

const getFeedFilterColumnsBase = (
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings
): Array<
    OptionalGridColumnDecl<
        FeedFilter,
        GridFeedFilterItemActions,
        GridColumnsOptions
    >
> => {
    const currency = mapStringToCurrencySymbol(clientCurrency);

    return [
        {
            title: '',
            key: GridFeedFiltersColumnKey.settings,
            cellClass: 'settings-cell',
            isSortable: false,
            mandatory: true,
            fixed: true,
            isAlignedToLeft: true,
            minWidth: isCompactViewEnabled ? 30 : 60,
            CellComponent: React.memo(
                ({
                    rowIndex,
                    actions
                }: GridCellProps<
                    FeedFilter,
                    GridFeedFilterItemActions
                >): JSX.Element => (
                    <GridActionsMenuContainer
                        index={rowIndex}
                        actions={actions}
                    />
                )
            )
        },
        {
            title: i18n('name'),
            key: GridFeedFiltersColumnKey.name,
            defaultSortDirection: SortDirection.asc,
            sortKey: 'filter_name',
            fixed: true,
            mandatory: true,
            minWidth: isCompactViewEnabled ? 100 : 150,
            CellComponent: React.memo(
                ({
                    rowData: feedFilter
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    const { name, id } = feedFilter;
                    return (
                        <GridFeedFilterNameCell
                            isCompactViewEnabled={isCompactViewEnabled}
                            name={name}
                            id={id}
                        />
                    );
                }
            ),
            isAlignedToLeft: true
        },
        {
            title: i18n('status'),
            key: GridFeedFiltersColumnKey.status,
            isSortable: false,
            minWidth: isCompactViewEnabled ? 60 : 90,
            CellComponent: React.memo(
                ({
                    rowData: feedFilter
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    const columnViewInfo =
                        columnsSettings.columnViewInfo[
                            GridFeedFiltersColumnKey.status
                        ];
                    const {
                        status,
                        group: { campaign },
                        targetFunnel,
                        id
                    } = feedFilter;

                    return (
                        <FeedFilterStatusCell
                            status={status}
                            id={id}
                            campaignPlatform={campaign.strategy.platform}
                            targetFunnel={targetFunnel}
                            isTextVisible={isStatusTextVisible(
                                isCompactViewEnabled,
                                columnViewInfo
                            )}
                        />
                    );
                }
            ),
            isAlignedToLeft: true
        },
        {
            title: i18n('target-funnel'),
            key: GridFeedFiltersColumnKey.targetFunnel,
            isSortable: false,
            minWidth: isCompactViewEnabled ? 200 : 250,
            CellComponent: React.memo(
                ({
                    rowData: feedFilter
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    return (
                        <TextCell>
                            {getFunnelTypeName(feedFilter.targetFunnel)}
                        </TextCell>
                    );
                }
            )
        },
        {
            title: i18n('cpc', { currency }),
            key: GridFeedFiltersColumnKey.cpc,
            sortKey: 'price_cpc',
            align: 'right',
            minWidth: isCompactViewEnabled ? 100 : 120,
            CellComponent: React.memo(
                ({
                    rowData: { budgetCalculation, priceCpc, id }
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    const isAvailable =
                        budgetCalculation !== SmartGroupBudgetCalculation.AUTO;

                    if (!isAvailable) {
                        return <TextCell>—</TextCell>;
                    }

                    return (
                        <FeedFilterRateCellEditorContainer
                            fixedRateType={FeedFilterRateType.cpc}
                            feedFilterId={id}
                            rate={priceCpc}
                            cellName={'priceCpc'}
                        />
                    );
                }
            )
        },
        {
            title: i18n('cpa', { currency }),
            key: GridFeedFiltersColumnKey.cpa,
            sortKey: 'price_cpa',
            align: 'right',
            minWidth: isCompactViewEnabled ? 100 : 120,
            CellComponent: React.memo(
                ({
                    rowData: { budgetCalculation, priceCpa, id }
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    const isAvailable =
                        budgetCalculation === SmartGroupBudgetCalculation.FULL;
                    if (!isAvailable) {
                        return <TextCell>—</TextCell>;
                    }

                    return (
                        <FeedFilterRateCellEditorContainer
                            fixedRateType={FeedFilterRateType.cpa}
                            feedFilterId={id}
                            rate={priceCpa}
                            cellName={'priceCpa'}
                        />
                    );
                }
            )
        },
        {
            title: i18n('autobudget-priority'),
            key: GridFeedFiltersColumnKey.autobudgetPriority,
            sortKey: 'autobudget_priority',
            width: 125,
            CellComponent: React.memo(
                ({
                    rowData: { autobudgetPriority, budgetCalculation, id }
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    const isAvailable =
                        budgetCalculation === SmartGroupBudgetCalculation.AUTO;

                    if (!isAvailable) {
                        return <TextCell>—</TextCell>;
                    }

                    return (
                        <FeedFilterPriorityCellEditorContainer
                            feedFilterId={id}
                            cellName={'priority'}
                            autobudgetPriority={autobudgetPriority}
                        />
                    );
                }
            )
        },
        {
            title: i18n('campaign-group'),
            key: GridFeedFiltersColumnKey.campaignGroup,
            minWidth: isCompactViewEnabled ? 100 : 150,
            sortKey: 'campaign_id',
            CellComponent: React.memo(
                ({
                    rowData: { group }
                }: GridCellProps<FeedFilter>): JSX.Element => (
                    <GridParentCell campaign={group.campaign} group={group} />
                )
            )
        },
        {
            title: i18n('banners'),
            key: GridFeedFiltersColumnKey.banners,
            sortKey: 'group_id',
            minWidth: isCompactViewEnabled ? 200 : 320,
            CellComponent: React.memo(
                ({
                    rowData: { mainBanner, bannersTotalCount, id }
                }: GridCellProps<FeedFilter>): JSX.Element => {
                    if (!mainBanner) {
                        return <TextCell>—</TextCell>;
                    }

                    const {
                        bannerType,
                        group: {
                            id: groupId,
                            campaign: { id: campaignId }
                        }
                    } = mainBanner;
                    const isTextBanner = isTextBannerCheck(bannerType);

                    return (
                        <GridBannerPreviewCellContainer
                            id={id}
                            campaignId={campaignId}
                            groupId={groupId}
                            bannersTotalCount={bannersTotalCount}
                            isTextBanner={isTextBanner}
                        >
                            <BannerPreview bannerPreviewData={mainBanner} />
                        </GridBannerPreviewCellContainer>
                    );
                }
            )
        }
    ];
};

export function getDefaultDynamicFeedFilterColumnsFields(): ColumnsSettings {
    return {
        columnOrder: [
            GridFeedFiltersColumnKey.settings,
            GridFeedFiltersColumnKey.name,
            GridFeedFiltersColumnKey.status,
            GridFeedFiltersColumnKey.targetFunnel,
            GridFeedFiltersColumnKey.cpc,
            GridFeedFiltersColumnKey.cpa,
            GridFeedFiltersColumnKey.autobudgetPriority,
            GridFeedFiltersColumnKey.campaignGroup,
            GridFeedFiltersColumnKey.banners,
            StatKey.STAT_COST,
            StatKey.STAT_AVG_CLICK_COST,
            StatKey.STAT_GOALS
        ],
        columnViewInfo: {
            [GridFeedFiltersColumnKey.name]: {
                width: 250,
                widthCompact: 200,
                hidden: false
            },
            [GridFeedFiltersColumnKey.settings]: {
                width: 30,
                widthCompact: 30,
                hidden: false
            },
            [GridFeedFiltersColumnKey.status]: {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            [GridFeedFiltersColumnKey.targetFunnel]: {
                width: 250,
                widthCompact: 200,
                hidden: false
            },
            [GridFeedFiltersColumnKey.cpc]: {
                width: 120,
                widthCompact: 100,
                hidden: false
            },
            [GridFeedFiltersColumnKey.cpa]: {
                width: 120,
                widthCompact: 100,
                hidden: false
            },
            [GridFeedFiltersColumnKey.autobudgetPriority]: {
                width: 125,
                widthCompact: 125,
                hidden: false
            },
            [GridFeedFiltersColumnKey.campaignGroup]: {
                width: 275,
                widthCompact: 200,
                hidden: false
            },
            [GridFeedFiltersColumnKey.banners]: {
                width: 500,
                widthCompact: 400,
                hidden: false
            },
            [StatKey.STAT_COST]: {
                width: 135,
                widthCompact: 90,
                hidden: false,
                totalKey: GridScreenTotalKey.cost
            },
            [StatKey.STAT_AVG_CLICK_COST]: {
                width: 175,
                widthCompact: 100,
                hidden: false
            },
            [StatKey.STAT_GOALS]: {
                width: 140,
                widthCompact: 80,
                hidden: false
            }
        }
    };
}

export function getFeedFilterColumns(
    options: GridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean
): Array<GridColumnDecl<FeedFilter>> {
    const result = getFeedFilterColumnsBase(
        clientCurrency,
        isCompactViewEnabled,
        columnsSettings[feedFilters]
    )
        .concat(
            getStaticStatsColumns<FeedFilter>(
                {
                    getCurrency: (item: FeedFilter): CurrencyCode =>
                        item.group.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                false
            )
        )
        .map(column => {
            return {
                ...column,
                ...columnsSettings[feedFilters].columnViewInfo[column.key]
            };
        });

    return arrangeInOrder(result, columnsSettings[feedFilters].columnOrder);
}
